import type { CardIdentity, CardPresentation } from '../shared/types';

const EBAY_SEARCH_BASE = 'https://www.ebay.com/sch/i.html';
export const POINT130_SEARCH_URL = 'https://130point.com';
const BASE_VARIATION_LABELS = new Set([
  'base',
  'base version',
  'no parallel',
  'no parallel / base version',
]);

function researchParallel(identity: Partial<CardIdentity>): string | undefined {
  const variation = identity.variation?.trim();
  if (variation === undefined || variation === '') return undefined;
  return BASE_VARIATION_LABELS.has(variation.toLowerCase()) ? undefined : variation;
}

/** Build a concise query from discriminating card identity fields. */
export function cardResearchQuery(
  identity: Partial<CardIdentity>,
  presentation?: CardPresentation,
): string {
  const year = identity.year?.trim();
  const set = identity.set?.trim();
  const setAlreadyIncludesYear =
    year !== undefined &&
    year !== '' &&
    set !== undefined &&
    set.split(/\s+/).some((token) => token === year);
  const parts = [
    setAlreadyIncludesYear ? undefined : year,
    set,
    identity.name,
    identity.number ? `#${identity.number}` : undefined,
    researchParallel(identity),
    presentation?.kind === 'GRADED' ? `${presentation.grader} ${presentation.grade}` : undefined,
  ];
  return [
    ...new Set(
      parts.map((value) => value?.trim()).filter((value): value is string => Boolean(value)),
    ),
  ].join(' ');
}

export function ebaySearchUrl(query: string, sold = false): string {
  const params = new URLSearchParams({
    _nkw: query.trim(),
    ...(sold ? { LH_Complete: '1', LH_Sold: '1' } : {}),
  });
  return `${EBAY_SEARCH_BASE}?${params.toString()}`;
}

/** Parse a positive USD input exactly, without routing currency through a float. */
export function usdInputToCents(value: string): number | null {
  const match = /^\s*\$?(\d{1,7})(?:\.(\d{1,2}))?\s*$/.exec(value);
  if (match === null) return null;
  const dollars = Number(match[1]);
  const cents = Number((match[2] ?? '').padEnd(2, '0'));
  const amount = dollars * 100 + cents;
  return amount >= 1 && amount <= 100_000_000 ? amount : null;
}

export function centsToUsdInput(amountMinor?: number): string {
  return amountMinor === undefined ? '' : (amountMinor / 100).toFixed(2);
}
